import type { CommunityScorePercentageType } from "@/components/community-score-wheel";
import type { CategoryType } from "@/components/game-categories";
import type { OverallRatingEnum } from "@/components/rate-options";
import type { UserType } from "@/components/user-search-modal/types";
import type { GameKindEnum } from "@/pages/search/types";

interface ImageType {
  id: string;
  imageId?: string;
}

export interface PictureType {
  id: string;
  imageId?: string;
  imageKey?: string;
}

export const TabEnum = {
  REVIEW: "Review",
  TEAM: "Details",
} as const;
export type TabEnum = (typeof TabEnum)[keyof typeof TabEnum];

export type TabKeysType = keyof typeof TabEnum;

export const PrivacyEnum = {
  // FOLLOWING: 'FOLLOWING',
  // INNER_CIRCLE: 'INNER_CIRCLE',
  PRIVATE: "PRIVATE",
  PUBLIC: "PUBLIC",
} as const;
export type PrivacyEnum = (typeof PrivacyEnum)[keyof typeof PrivacyEnum];

interface EscapeReviewComponentType {
  id: string;
  response?: {
    comment?: string;
    optionId?: string;
  };
}

interface ReviewType {
  components?: EscapeReviewComponentType[];
  id: string;
  images?: PictureType[];
  isContainingSpoilers: boolean;
  overallRating?: keyof typeof OverallRatingEnum;
  privacy?: PrivacyEnum;
  privateNotes?: string;
  summary?: string;
}

export interface EscapeType {
  escapeTime?: string;
  gameDate?: string;
  id: string;
  isEscaped?: boolean;
  myReview?: ReviewType;
  pictures: PictureType[];
  taggedUsers: UserType[];
}

export interface GameType {
  communityRatingCount?: number;
  communityScoreBucket?: string;
  communityScorePercentages?: CommunityScorePercentageType[];
  difficulty?: string;
  gameimageSet?: ImageType[];
  id: string;
  kind: GameKindEnum;
  location: {
    id: string;
    company: {
      id: string;
      name: string;
      slug?: string;
    };
    displayName?: string;
    slug?: string;
  };
  minutes: number;
  myEscape?: EscapeType;
  name: string;
  playersMax: number;
  playersMin: number;
  primaryCategory?: CategoryType;
  slug?: string;
}

export const ReviewComponentTypeEnum = {
  OPTIONS: "OPTIONS",
  RATING: "RATING",
  TEXT: "TEXT",
} as const;
export type ReviewComponentTypeEnum =
  (typeof ReviewComponentTypeEnum)[keyof typeof ReviewComponentTypeEnum];

export interface ReviewComponentOptionType {
  id: string;
  value: string;
}

export interface ReviewComponentType {
  description?: string;
  id: string;
  isCommentable: boolean;
  isHighlighted: boolean;
  options: ReviewComponentOptionType[];
  question: string;
  type: ReviewComponentTypeEnum;
}

export interface GameDataType {
  game: GameType;
  reviewComponents?: ReviewComponentType[];
}

export interface GameVariablesType {
  id: string;
  isUser: boolean;
}

export interface EscapeMutationInput {
  escapeTime?: string;
  game?: string;
  gameDate?: string;
  id?: string;
  isEscaped?: boolean;
  pictures?: string[];
  taggedUsers?: string[];
}

export interface ComponentResponseInput {
  comment?: string;
  option?: string;
  question: string;
}

export type NormalizedComponentResponse = Record<
  string,
  ComponentResponseInput
>;

export interface ExperienceReviewMutationInput {
  components?: ComponentResponseInput[];
  images?: string[];
  isContainingSpoilers?: boolean;
  overallRating?: keyof typeof OverallRatingEnum;
  privacy: PrivacyEnum;
  privateNotes?: string;
  summary?: string;
}

export interface JoinEscapeDataType {
  joinEscape: {
    escape?: EscapeType;
  };
}

export interface JoinEscapeVariablesType {
  escapeId: string;
  review?: ExperienceReviewMutationInput;
  taggedUsers?: string[];
}

export interface UpdateEscapeDataType {
  updateEscape: {
    escape?: EscapeType;
  };
}

export interface UpdateEscapeVariablesType {
  escape: EscapeMutationInput;
  experienceReview?: ExperienceReviewMutationInput;
}
